import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSystemStatus } from '../contexts/SystemStatusContext';
import { useAdminMode } from '../contexts/AdminModeContext';
import { useFontSize } from '../hooks/useFontSize';
import { useLimitHandler } from '../hooks/useLimitHandler';
import { useAppVersion } from '../hooks/useAppVersion';
import InventoryView from './InventoryView';
import ProfileView from './ProfileView';
import PeopleView from './PeopleView';
import AdminDashboard from './AdminDashboard';
import HelpPage from './HelpPage';
import MarketTab from './MarketTab';
import RecipesTab from './RecipesTab';
import ProfessionalTab from './ProfessionalTab';
import AppContextSheet from './AppContextSheet';
import ViewSettingsModal from './ViewSettingsModal';
import UpgradePromptModal from './UpgradePromptModal';
import PlansPage from './PlansPage';
import QuickStartGuide from './QuickStartGuide';
import BottomNavigation from './BottomNavigation';
import PageTemplate from './PageTemplate';
import { v4 as uuidv4 } from 'uuid';
import '../styles/global.css';
import '../styles/layout.css';
import '../styles/components.css';
import '../styles/auth.css';
import '../styles/results.css';
import '../styles/pwa.css';
import '../styles/components/home.css';
import '../styles/components/scan.css';
import '../styles/components/inventory.css';
import '../styles/voice-listening.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config';
import { useContent } from '../hooks/useContent';
import { Platform } from '../utils/platform';
import { useMode, ModeType } from '../contexts/ModeContext';
import { useSpace } from '../contexts/SpaceContext';

export interface DetectedItem {
  id?: number;
  uuid?: string;
  name: string;
  description: string;
  category: string;
  color: string;
  area?: string;
  location?: string;
  sub_location?: string;
  photo_location?: string;
  quantity?: number;
  sale_price?: number;
  stock_keeping?: boolean;
  quantity_min?: number;
  purchase_price?: number;
  purchased_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AnalysisResult {
  items: DetectedItem[];
  summary: string;
  total_items: number;
  error?: string;
  image?: { id?: number; url?: string; width?: number; height?: number; checksum?: string };
}

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const { user, token, loading } = useAuth();
  const { isMaintenanceMode } = useSystemStatus();
  const { adminModeEnabled } = useAdminMode();
  const { app, inventory, common } = useContent();
  const { fontSize, setFontSize } = useFontSize();
  const { showUpgradeModal, limitType, limitMessage, checkResponseForLimit, closeUpgradeModal } = useLimitHandler();
  const { activeMode, setActiveMode, groceryCollectionId, ensureGroceryCollection, resetGroceryCollection } = useMode();
  const { activeSpace, spaces, setActiveSpace } = useSpace();
  
  useEffect(() => {
    if (isMaintenanceMode) {
      navigate('/help');
    }
  }, [isMaintenanceMode, navigate]);

  const [isLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [itemsSaved, setItemsSaved] = useState(false);
  const [showPlansPage, setShowPlansPage] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'inventory' | 'groups' | 'profile' | 'market'>(() => {
    const saved = localStorage.getItem('app-activeTab');
    if (saved === 'view') return 'market';
    return (saved as 'home' | 'inventory' | 'groups' | 'profile' | 'market') || 'inventory';
  });
  const [showViewSettings, setShowViewSettings] = useState(false);
  const [inventoryCount, setInventoryCount] = useState({ total: 0, filtered: 0, selected: 0, shared: 0, completed: 0, isSelectMode: false });
  const [trashCount, setTrashCount] = useState({ total: 0, filtered: 0, selected: 0 });
  const [inventoryRefreshTrigger, setInventoryRefreshTrigger] = useState(0);
  const [hasQueueItems, setHasQueueItems] = useState(false);
  const [navigateToCollectionId, setNavigateToCollectionId] = useState<number | null>(null);
  const [filterByGroupId, setFilterByGroupId] = useState<number | null>(null);
  const [lastQueueState, setLastQueueState] = useState<any[]>([]);
  const [isStockEditMode, setIsStockEditMode] = useState(false);
  const [hasUnsavedStockChanges, setHasUnsavedStockChanges] = useState(false);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [showQuickStartGuide, setShowQuickStartGuide] = useState(false);
  const [showAppContext, setShowAppContext] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState<{ name: string; description: string | null; search_string: string } | null>(null);
  const toggleCollectionSheetRef = useRef<(() => void) | null>(null);
  
  const prevTabRef = useRef<'home' | 'inventory' | 'groups' | 'profile' | 'market'>(activeTab);
  
  const openViewSettings = useCallback(() => {
    setShowViewSettings(true);
  }, []);
  
  const getFontSizeNumber = () => {
    const sizeMap = { 'small': '1', 'medium': '2', 'large': '3', 'x-large': '4' };
    return sizeMap[fontSize];
  };
  
  const handleInventoryCountChange = useCallback((total: number, filtered: number, selected: number, shared: number, completed: number, isSelectMode: boolean = false) => {
    setInventoryCount({ total, filtered, selected, shared, completed, isSelectMode });
  }, []);

  const handleTrashCountChange = useCallback((total: number, filtered: number, selected: number) => {
    setTrashCount({ total, filtered, selected });
  }, []);
  
  useEffect(() => {
    if (Platform.isNative) {
      document.documentElement.classList.add('capacitor-app');
      document.body.classList.add('capacitor-app');
    }
  }, []);
  
  const handleLoginSuccess = useCallback(async () => {
    setTimeout(async () => {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) return;
      
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('help-tip-dismissed-')) {
          localStorage.removeItem(key);
        }
      });
      
      try {
        const defaultSpaceId = await fetch(`${API_BASE_URL}/api/spaces`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }).then(res => res.json()).then(data => data.spaces?.[0]?.id);

        const spaceParam = defaultSpaceId ? `?space_id=${defaultSpaceId}` : '';
        const collectionsResponse = await fetch(`${API_ENDPOINTS.GET_COLLECTIONS()}${spaceParam}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        const lastCollection = localStorage.getItem('inventory-collection');
        if (lastCollection && lastCollection !== 'all' && lastCollection !== 'shared_with_me' && 
            lastCollection !== 'wandering' && !lastCollection.startsWith('shared_with_group_') &&
            collectionsResponse.ok) {
          const collectionsData = await collectionsResponse.json();
          const collectionId = lastCollection.replace('shared-', '');
          const collectionExists = collectionsData.collections?.some(
            (c: any) => String(c.id) === collectionId
          );
          
          if (!collectionExists) {
            localStorage.setItem('inventory-collection', 'all');
          }
        }
        
        const response = await fetch(`${API_ENDPOINTS.GET_ITEMS}${spaceParam}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          const itemCount = data.items?.length || 0;
          
          const userId = authToken ? JSON.parse(atob(authToken.split('.')[1])).user_id : null;
          const hasSeenGuide = localStorage.getItem(`hasSeenQuickStartGuide_${userId}`);
          
          if (!hasSeenGuide && itemCount > 0 && itemCount <= 15) {
            if (collectionsResponse.ok) {
              const collectionsData = await collectionsResponse.json();
              const groceryCollection = collectionsData.collections?.find(
                (c: any) => c.name === '🍋Grocery'
              );
              
              if (groceryCollection) {
                sessionStorage.setItem('newUserGroceryCollectionId', String(groceryCollection.id));
              }
            }
            
            setShowQuickStartGuide(true);
            setActiveTab('inventory');
            localStorage.setItem('app-activeTab', 'inventory');
            if (userId) localStorage.setItem(`hasSeenQuickStartGuide_${userId}`, 'true');
          } else if (itemCount > 0) {
            setActiveTab('inventory');
            localStorage.setItem('app-activeTab', 'inventory');
          } else {
            setActiveTab('home');
            localStorage.setItem('app-activeTab', 'home');
          }
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      }
    }, 100);
  }, []);
  
  const lastUserIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (user && token && !loading) {
      const currentUserId = user.id;
      if (lastUserIdRef.current !== currentUserId) {
        lastUserIdRef.current = currentUserId;
        handleLoginSuccess();
        if (activeMode === 'kitchen') {
          ensureGroceryCollection(token, activeSpace?.id);
        }
      }
    }
    if (!user && !token) {
      lastUserIdRef.current = null;
    }
  }, [user, token, loading, handleLoginSuccess, activeMode, ensureGroceryCollection]);

  // Ensure the correct grocery collection is loaded whenever the user enters
  // Kitchen mode OR switches spaces while already in Kitchen mode.
  // We track the previous space so we can clear the stale groceryCollectionId
  // immediately when the space changes (prevents briefly showing wrong items).
  const prevSpaceIdRef = React.useRef(activeSpace?.id);
  useEffect(() => {
    const spaceChanged = prevSpaceIdRef.current !== activeSpace?.id;
    prevSpaceIdRef.current = activeSpace?.id;

    if (spaceChanged) {
      // Always clear the cached grocery collection when the space changes,
      // regardless of the current mode. This prevents stale cross-space data.
      resetGroceryCollection();
    }

    if (activeMode === 'kitchen' && token && activeSpace?.id) {
      const spaceId = activeSpace.id;
      let cancelled = false;
      (async () => {
        for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
          const result = await ensureGroceryCollection(token, spaceId);
          if (result || cancelled) break;
          // Wait before retry (skip wait on last attempt)
          if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
        }
      })();
      return () => { cancelled = true; };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, activeSpace?.id, token]);

  // Self-healing fallback: if we're in kitchen mode but groceryCollectionId is
  // still null (e.g. the space-change effect didn't resolve it), keep retrying.
  useEffect(() => {
    if (activeMode !== 'kitchen' || groceryCollectionId || !token || !activeSpace?.id) return;
    const spaceId = activeSpace.id;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      console.log(`[AppContent] Fallback retry #${attempts}: groceryCollectionId still null in kitchen mode`);
      const result = await ensureGroceryCollection(token, spaceId);
      if (result || attempts >= 5) clearInterval(interval);
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, groceryCollectionId, token, activeSpace?.id]);
  
  useEffect(() => {
    if (!token || !hasQueueItems) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(API_ENDPOINTS.QUEUE_PENDING, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) return;
        
        const data = await response.json();
        const pendingItems = data.queue || [];
        
        const newlyCompleted = pendingItems.filter((newItem: any) => {
          const oldItem = lastQueueState.find((old: any) => old.id === newItem.id);
          return oldItem && oldItem.status !== 'completed' && newItem.status === 'completed';
        });
        
        if (newlyCompleted.length > 0) {
          setInventoryRefreshTrigger(prev => prev + 1);
        }
        
        setLastQueueState(pendingItems);
        
        const hasActive = pendingItems.some((item: any) => 
          item.status === 'pending' || item.status === 'analyzing' || item.status === 'saving'
        );
        setHasQueueItems(hasActive);
        
      } catch (e) {
        console.error('Error polling queue:', e);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [token, hasQueueItems, lastQueueState]);
  
  useEffect(() => {
    const mc = document.querySelector('.main-content') as HTMLElement | null;
    if (!mc) return;

    if (activeTab !== 'inventory') {
      mc.classList.remove('filters-hidden');
    }

    const inputs = Array.from(document.querySelectorAll('.page-template-input')) as HTMLElement[];
    inputs.forEach(i => i.classList.remove('input-hidden'));

    prevTabRef.current = activeTab;

    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [activeTab]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/help', { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    const handleOpenAppContext = () => setShowAppContext(true);
    window.addEventListener('openSpaceSelector', handleOpenAppContext);
    return () => window.removeEventListener('openSpaceSelector', handleOpenAppContext);
  }, []);

  if (loading || (!loading && !user)) {
    return (
      <div className="app-loading-screen">
        <div className="app-loading-inner">
          <div className="app-loading-spinner"></div>
          {loading && <p className="app-loading-text">{common.loading}</p>}
        </div>
      </div>
    );
  }

  const handleTabChange = (tab: 'home' | 'inventory' | 'groups' | 'profile' | 'market') => {
    if (tab === 'inventory' && activeTab === 'inventory') {
      toggleCollectionSheetRef.current?.();
      return;
    }
    
    setActiveTab(tab);
    localStorage.setItem('app-activeTab', tab);
  };

  const handleOpenAppContext = () => {
    setShowAppContext(true);
  };

  const handleOpenCollectionSheet = () => {
    if (activeTab !== 'inventory') {
      setActiveTab('inventory');
      localStorage.setItem('app-activeTab', 'inventory');
    }
    toggleCollectionSheetRef.current?.();
  };

  const handleModeChange = async (mode: ModeType) => {
    setActiveMode(mode);
    if (mode === 'kitchen' && token) {
      const id = await ensureGroceryCollection(token, activeSpace?.id);
      if (id) {
        setNavigateToCollectionId(id);
        setActiveTab('inventory');
        setTimeout(() => setNavigateToCollectionId(null), 100);
      }
    }
  };

  const handleActivateRecipe = (bindle: { name: string; description: string | null; search_string: string }) => {
    setPendingRecipe(bindle);
    setActiveTab('inventory');
    setTimeout(() => setPendingRecipe(null), 300);
  };

  const handleNavigateToCollection = (collectionId: number) => {
    setNavigateToCollectionId(collectionId);
    setActiveTab('inventory');
    setTimeout(() => setNavigateToCollectionId(null), 100);
  };

  const handleNavigateToInventory = (groupId: number) => {
    setFilterByGroupId(groupId);
    setActiveTab('inventory');
    setTimeout(() => setFilterByGroupId(null), 100);
  };

  return (
    <div className={`App mobile-app ${activeTab}-active`}>
      <div
        className={`main-content ${activeTab === 'profile' ? 'profile-active' : ''} ${activeTab === 'inventory' ? 'items-active' : ''} ${activeTab === 'groups' ? 'groups-active' : ''}`}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <div className="tab-scroll-container home-tab" style={{ display: activeTab === 'home' ? 'block' : 'none' }}>
          {adminModeEnabled ? (
            <PageTemplate
              hasGrayBackground={false}
              enableSmartScroll={false}
              stickyHeader={false}
              className="admin-page"
            >
              <AdminDashboard />
            </PageTemplate>
          ) : (
            <HelpPage 
              isActive={activeTab === 'home'}
              onTabChange={handleTabChange}
              onFontSizeToggle={openViewSettings}
              fontSize={getFontSizeNumber()}
              onOpenAppContext={handleOpenAppContext}
            />
          )}
        </div>
        
        <div className="tab-scroll-container inventory-tab" style={{ display: activeTab === 'inventory' ? 'block' : 'none' }}>
            <InventoryView 
            isActive={activeTab === 'inventory'}
            onInventoryCountChange={handleInventoryCountChange} 
            refreshTrigger={inventoryRefreshTrigger}
            onStockEditStateChange={(isEditing, hasUnsaved) => {
              setIsStockEditMode(isEditing);
              setHasUnsavedStockChanges(hasUnsaved);
            }}
            onVoiceListeningChange={setIsVoiceListening}
            navigateToCollection={navigateToCollectionId}
            filterByGroupId={filterByGroupId}
            checkResponseForLimit={checkResponseForLimit}
            onFontSizeToggle={openViewSettings}
            fontSize={getFontSizeNumber()}
            onSockClick={() => {
              const event = new CustomEvent('sockIconClick');
              window.dispatchEvent(event);
            }}
            isVoiceListening={isVoiceListening}
            onNotificationNavigateToCollection={handleNavigateToCollection}
            onNotificationNavigateToGroup={handleNavigateToInventory}
            onRegisterToggleSheet={(fn) => { toggleCollectionSheetRef.current = fn; }}
            lockedCollectionId={activeMode === 'kitchen' ? groceryCollectionId : null}
            onOpenAppContext={handleOpenAppContext}
            onOpenCollectionSheet={handleOpenCollectionSheet}
            pendingRecipe={pendingRecipe}
            hideKitchenCollections={activeMode !== 'kitchen'}
            />
        </div>
        
        <div className="tab-scroll-container groups-tab" style={{ display: activeTab === 'groups' ? 'block' : 'none' }}>
          <PeopleView 
            isActive={activeTab === 'groups'}
            onNavigateToCollection={handleNavigateToCollection} 
            onNavigateToInventory={handleNavigateToInventory}
            onFontSizeToggle={openViewSettings}
            fontSize={getFontSizeNumber()}
            onOpenAppContext={handleOpenAppContext}
          />
        </div>
        
        <div className="tab-scroll-container jung-tab" style={{ display: activeTab === 'profile' ? 'block' : 'none' }}>
          <ProfileView 
            isActive={activeTab === 'profile'}
            onNavigateToCollection={handleNavigateToCollection} 
            onTrashCountChange={handleTrashCountChange}
            onViewPlans={() => setShowPlansPage(true)}
            onFontSizeToggle={openViewSettings}
            fontSize={getFontSizeNumber()}
            onShowQuickStartGuide={() => setShowQuickStartGuide(true)}
            onOpenAppContext={handleOpenAppContext}
          />
        </div>
        
        <div className="tab-scroll-container market-tab" style={{ display: activeTab === 'market' ? 'block' : 'none' }}>
          {activeMode === 'kitchen' ? (
            <RecipesTab
              isActive={activeTab === 'market'}
              groceryCollectionId={groceryCollectionId}
              onActivateRecipe={handleActivateRecipe}
              onFontSizeToggle={openViewSettings}
              fontSize={getFontSizeNumber()}
              activeMode={activeMode}
              onOpenAppContext={handleOpenAppContext}
            />
          ) : activeMode === 'professional' ? (
            <ProfessionalTab
              isActive={activeTab === 'market'}
              onTabChange={handleTabChange}
              onFontSizeToggle={openViewSettings}
              fontSize={getFontSizeNumber()}
              activeMode={activeMode}
              onOpenAppContext={handleOpenAppContext}
            />
          ) : (
            <MarketTab
              isActive={activeTab === 'market'}
              onFontSizeToggle={openViewSettings}
              fontSize={getFontSizeNumber()}
              activeMode={activeMode}
              onOpenAppContext={handleOpenAppContext}
            />
          )}
        </div>
      </div>

      {!(isStockEditMode && hasUnsavedStockChanges) && (
        <BottomNavigation 
          activeTab={activeTab}
          onTabChange={handleTabChange}
          activeMode={activeMode}
        />
      )}

      <AppContextSheet
        isOpen={showAppContext}
        activeMode={activeMode}
        onModeSelect={(mode: ModeType) => {
          handleModeChange(mode);
        }}
        onClose={() => setShowAppContext(false)}
      />

      {limitType && (
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={closeUpgradeModal}
          limitType={limitType}
          message={limitMessage || undefined}
          onViewPlans={() => setShowPlansPage(true)}
        />
      )}

      {showPlansPage && (
        <PlansPage onClose={() => setShowPlansPage(false)} />
      )}

      {showViewSettings && (
        <ViewSettingsModal
          onClose={() => setShowViewSettings(false)}
          fontSize={fontSize}
          onFontSizeChange={setFontSize}
        />
      )}

      {showQuickStartGuide && (
        <QuickStartGuide
          onComplete={() => {
            const authToken = localStorage.getItem('auth_token');
            const userId = authToken ? JSON.parse(atob(authToken.split('.')[1])).user_id : null;
            if (userId) {
              localStorage.setItem(`hasSeenQuickStartGuide_${userId}`, 'true');
            }
            setShowQuickStartGuide(false);
          }}
          onSkip={() => {
            const authToken = localStorage.getItem('auth_token');
            const userId = authToken ? JSON.parse(atob(authToken.split('.')[1])).user_id : null;
            if (userId) {
              localStorage.setItem(`hasSeenQuickStartGuide_${userId}`, 'true');
            }
            setShowQuickStartGuide(false);
          }}
        />
      )}
    </div>
  );
};

export default AppContent;
